import VoltieUser from '../../types/VoltieUser'
import { flagIcon, languageByCode } from '../../languages'

// Dates are shown as zero-padded YYYY.MM.DD everywhere on the Users pages (language-independent).
function pad(n: number): string { return n < 10 ? `0${n}` : `${n}` }
function toDate(v?: string | number | null): Date | null {
  if (v == null || v === '') return null
  if (typeof v === 'number') { const d = new Date(v); return isNaN(d.getTime()) ? null : d }
  const s = String(v).trim()
  let ms = Date.parse(s)
  if (isNaN(ms)) ms = Date.parse(s.replace(/\./g, '-').replace(' ', 'T')) // handle "YYYY.MM.DD HH:MM:SS"
  return isNaN(ms) ? null : new Date(ms)
}
function ymd(d: Date): string { return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}` }
function hms(d: Date): string { return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` }

export { normalizeCurrency } from '../../utils/currency'

// fcm tokens (and push tokens) must never be shown in full. Defensive against non-string input.
export function maskToken(token?: string): string {
  if (!token || typeof token !== 'string') return '-'
  if (token.length <= 10) return `${token.slice(0, 2)}…${token.slice(-2)}`
  return `${token.slice(0, 4)}…${token.slice(-4)}`
}

// An fcm_tokens entry is either a plain token string, or { platform, token, updated_at }.
export function fcmTokenInfo(v?: string | { platform?: string; token?: string; updated_at?: number }): { token: string; platform?: string; updatedAt?: number } {
  if (typeof v === 'string') return { token: v }
  if (v && typeof v === 'object') return { token: v.token || '', platform: v.platform, updatedAt: v.updated_at }
  return { token: '' }
}

// Provider enum real values: Email | Gmail | Password | Apple | Google. Map to an icon + display label.
export function providerMeta(providerEnum?: string): { icon: string; label: string } {
  switch ((providerEnum || '').toLowerCase()) {
    case 'apple': return { icon: 'login.appleLogoSmall', label: 'Apple' }
    case 'google':
    case 'gmail': return { icon: 'login.googleLogoSmall', label: 'Google' }
    case 'email':
    case 'password': return { icon: 'app.mail', label: 'Email' }
    default: return { icon: 'app.mail', label: providerEnum || '-' }
  }
}

// access_lvl -> existing translation key (reuse charger_userAccessLevel_*).
export function accessLevelLabelKey(lvl?: number): string {
  return `charger_userAccessLevel_${lvl ?? 0}`
}

export function fullName(user?: VoltieUser | null): string {
  if (!user) return '-'
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
  return name || user.nickname || user.email || '-'
}

// Parse an ISO-ish timestamp; returns ms or null.
function toMs(v?: string): number | null {
  if (!v) return null
  const t = Date.parse(v)
  return isNaN(t) ? null : t
}

// Approx account creation (no Auth access): earliest of accepted_terms_version / platform_logins.frontend.
export function approxCreatedMs(user?: VoltieUser | null): number | null {
  if (!user) return null
  const candidates = [toMs(user.accepted_terms_version), toMs(user.platform_logins?.frontend)].filter((x): x is number => x != null)
  return candidates.length ? Math.min(...candidates) : null
}

// Approx last online (no Auth access): max of device last_login_date and all platform_logins timestamps.
export function approxLastOnlineMs(user?: VoltieUser | null): number | null {
  if (!user) return null
  const times: number[] = []
  for (const d of Object.values(user.devices || {})) {
    const t = toMs(d?.last_login_date)
    if (t != null) times.push(t)
  }
  for (const v of Object.values(user.platform_logins || {})) {
    const t = toMs(v)
    if (t != null) times.push(t)
  }
  return times.length ? Math.max(...times) : null
}

export function formatDate(v?: string | number | null): string {
  const d = toDate(v)
  return d ? ymd(d) : (typeof v === 'string' && v ? v : '-')
}

export function formatDateTime(v?: string | number | null): string {
  const d = toDate(v)
  return d ? `${ymd(d)} ${hms(d)}` : (typeof v === 'string' && v ? v : '-')
}

// Split a timestamp into date + time parts (for LabelDivided table cells).
export function dateTimeParts(v?: string | number | null): { date: string; time: string } {
  const d = toDate(v)
  if (!d) return { date: typeof v === 'string' && v ? v : '-', time: '' }
  return { date: ymd(d), time: hms(d) }
}

// A user's own language is written to the user record and read by the mobile apps. Both apps offer the
// same languages as Voltie Local (iOS Language, Android AppLanguage), so the dashboard's list is the
// user's list too (VLT-3184).
export function languageFlag(code?: string): string | null {
  return code && languageByCode(code.toLowerCase()) ? flagIcon(code.toLowerCase()) : null
}

export function languageName(code?: string): string {
  return code ? (languageByCode(code.toLowerCase())?.label || code) : '-'
}

// additional_email may be stored as an array or a map; normalize to an array of emails.
export function additionalEmails(v?: string[] | { [key: string]: string }): string[] {
  if (Array.isArray(v)) return v.filter(Boolean)
  if (v && typeof v === 'object') return Object.values(v).filter(Boolean)
  return []
}

// The user's chargers as an array with the charger id folded in.
export function chargerCount(user?: VoltieUser | null): number {
  return user?.chargers ? Object.keys(user.chargers).length : 0
}

// Normalize legacy top-level keys into the canonical shape (non-mutating copy of the affected bits).
export function normalizeUser(user: VoltieUser): VoltieUser {
  const provider_logins = { ...(user.provider_logins || {}) }
  if (user.login_google && !provider_logins.google) provider_logins.google = user.login_google
  if (user.login_email && !provider_logins.password) provider_logins.password = user.login_email
  const fcm_tokens = { ...(user.fcm_tokens || {}) }
  if (user.fcm_token && Object.keys(fcm_tokens).length === 0) fcm_tokens['legacy'] = user.fcm_token
  return {
    ...user,
    provider_logins,
    fcm_tokens,
    charger_id: user.charger_id || user.chargerid,
  }
}
